import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

/* App-wide UI: the toast ("Link kopiran", "Tema uklonjena · Poništi"), the
   desktop QR modal, the comments panel, and which layout is showing (mobile
   web below 700 px, spec 4.4). */
const UiContext = createContext(null)

export const MOBILE_QUERY = '(max-width: 699px)'
// Below this the desktop right column (search, Aktuelne teme) is hidden (spec 4.4).
export const NO_RAIL_QUERY = '(max-width: 1099px)'

export function useMediaQuery(query) {
  const get = () =>
    typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false
  const [matches, setMatches] = useState(get)
  useEffect(() => {
    if (!window.matchMedia) return undefined
    const mql = window.matchMedia(query)
    const onChange = () => setMatches(mql.matches)
    onChange()
    mql.addEventListener ? mql.addEventListener('change', onChange) : mql.addListener(onChange)
    return () =>
      mql.removeEventListener
        ? mql.removeEventListener('change', onChange)
        : mql.removeListener(onChange)
  }, [query])
  return matches
}

export function UiProvider({ children }) {
  const isMobile = useMediaQuery(MOBILE_QUERY)
  const noRail = useMediaQuery(NO_RAIL_QUERY)
  const [toast, setToast] = useState(null)
  const [qrOpen, setQrOpen] = useState(false)
  // Comments panel: { storyId, articleId } of the story whose comments are open.
  const [comments, setComments] = useState(null)
  const timer = useRef(null)

  /* notify('Link kopiran') or notify('Tema uklonjena', { label, onClick }) */
  const notify = useCallback((message, action = null) => {
    clearTimeout(timer.current)
    setToast({ message, action, id: Date.now() })
    timer.current = setTimeout(() => setToast(null), action ? 5000 : 2500)
  }, [])

  const dismissToast = useCallback(() => {
    clearTimeout(timer.current)
    setToast(null)
  }, [])

  useEffect(() => () => clearTimeout(timer.current), [])

  const value = useMemo(
    () => ({
      isMobile,
      noRail,
      toast,
      notify,
      dismissToast,
      qrOpen,
      openQr: () => setQrOpen(true),
      closeQr: () => setQrOpen(false),
      comments,
      openComments: (story) => setComments(story),
      closeComments: () => setComments(null),
    }),
    [isMobile, noRail, toast, notify, dismissToast, qrOpen, comments]
  )
  return <UiContext.Provider value={value}>{children}</UiContext.Provider>
}

export function useUi() {
  return useContext(UiContext)
}
